import React from 'react';
import { Box, Paper, Chip } from '@mui/material';
import { Check } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { MarkdownContent } from '../MarkdownContent';
import { MatchMessage, MatchInteractionType } from './types';

interface MatchMessageBubbleProps {
  message: MatchMessage;
}

export const MatchMessageBubble: React.FC<MatchMessageBubbleProps> = ({ message }) => {
  const theme = useTheme();

  const interactionType = getInteractionType(message);
  const isAnswered =
    !!message.selectedOptionId || !!message.selectedOptionIds || !!message.textResponse || !!message.infoResponses;

  // User message — right-aligned, colored bubble
  if (message.role === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: '85%',
            px: 2,
            py: 1.5,
            bgcolor: '#cc5544',
            color: 'white',
            borderRadius: 3,
            borderBottomRightRadius: 4,
            boxShadow: 1,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Paper>
      </Box>
    );
  }

  // Assistant message — left-aligned with markdown
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
      <Box sx={{ maxWidth: '90%' }}>
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            borderRadius: 3,
            borderBottomLeftRadius: 4,
            boxShadow: 1,
          }}
        >
          <MarkdownContent content={message.content} />
        </Paper>

        {/* Show completion chip after the user has responded via popup */}
        {interactionType && isAnswered && (
          <Box sx={{ mt: 1, px: 0.5 }}>
            <Chip
              icon={<Check sx={{ fontSize: 14 }} />}
              label="Responded"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Determine the effective interaction type from a message */
function getInteractionType(message: MatchMessage): MatchInteractionType | null {
  if (message.role === 'user') return null;
  if (message.interactionType) return message.interactionType;
  if (message.infoFields && message.infoFields.length > 0) return 'info_gather';
  if (message.textPrompt) return 'text_only';
  if (message.options && message.options.length > 0) {
    return 'choice_with_other';
  }
  return null;
}
